import type { Macro } from "@redotech/effect-api-shared/rpc/macro";
import { useSearch } from "@redotech/react-util/search";
import {
  isAtLeastOneMacroAutomationActive,
  MacroAutomation,
  type MacroAutomationsData,
  type MacroStatusToSet,
  type SnoozeDuration,
} from "@redotech/redo-model/macro";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  RedoList,
  type RedoListItem,
  type RedoListItemSelectedSource,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import Fuse from "fuse.js";
import { memo, useMemo, useRef, useState } from "react";
import * as styles from "./macro-selector-modal.module.css";

const searcher = new Fuse<Macro>([], { keys: ["name"], threshold: 0.5 });

export interface MacroSelectorModalProps {
  open: boolean;
  onClose(): void;
  onApplyMacro(macro: Macro): void;
  macros: readonly Macro[] | undefined;
  isApplying?: boolean;
}

export const MacroSelectorModal = memo(function MacroSelectorModal({
  open,
  onClose,
  onApplyMacro,
  macros,
  isApplying,
}: MacroSelectorModalProps) {
  const [searchValue, setSearchValue] = useState("");
  const [focusedMacro, setFocusedMacro] = useState<Macro | undefined>();

  const macroList = macros ?? [];

  const handleClose = () => {
    onClose();
    setSearchValue("");
    setFocusedMacro(undefined);
  };

  const handleApply = (macro: Macro) => {
    onApplyMacro(macro);
    handleClose();
  };

  return (
    <RedoModal
      isOpen={open}
      modalSize="lg"
      onModalCloseRequested={handleClose}
      primaryButton={
        focusedMacro
          ? {
              text: "Add to message",
              onClick: () => handleApply(focusedMacro),
              pending: isApplying,
              size: "md",
            }
          : undefined
      }
      title="Use template"
    >
      <Flex dir="row" height="full" overflow="hidden">
        <MacroSidebar
          key={open ? "open" : "closed"}
          macroFocused={setFocusedMacro}
          macros={macroList}
          macroSelected={handleApply}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
        />
        {focusedMacro ? (
          <MacroPreview macro={focusedMacro} />
        ) : (
          <MacroEmptyState />
        )}
      </Flex>
    </RedoModal>
  );
});

const MacroEmptyState = memo(function MacroEmptyState() {
  return (
    <Flex
      align="center"
      dir="column"
      flex="2"
      gap="lg"
      justify="center"
      p="7xl"
    >
      <Text textColor="tertiary">Select a template to preview</Text>
    </Flex>
  );
});

const MacroPreview = memo(function MacroPreview({ macro }: { macro: Macro }) {
  const automations = macroToAutomationsData(macro);
  const hasAutomations = isAtLeastOneMacroAutomationActive(automations);

  return (
    <Flex dir="column" overflow="auto">
      <Flex dir="column" overflowY="auto">
        <Flex dir="column" gap="xl" grow={1} overflow="auto" p="xl" shrink={0}>
          <Flex dir="column" gap="sm">
            <Text fontSize="lg" fontWeight="semibold">
              Template name
            </Text>
            <Text>{macro.name}</Text>
          </Flex>
          <Text fontSize="md" fontWeight="semibold">
            Message preview
          </Text>
          <div
            className={styles.htmlPreview}
            dangerouslySetInnerHTML={{
              __html: highlightMacroVariables(macro.htmlContent),
            }}
          />
        </Flex>
        <Divider />
        <Flex dir="column" gap="md" p="xl">
          <Text fontSize="md" fontWeight="semibold">
            Automations
          </Text>
          {hasAutomations ? (
            <AutomationBadges macro={macro} />
          ) : (
            <Text textColor="tertiary">No automations</Text>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});

const AutomationBadges = memo(function AutomationBadges({
  macro,
}: {
  macro: Macro;
}) {
  const badges: { key: string; label: string }[] = [];

  if (macro.statusToSet) {
    const statusLabel =
      macro.statusToSet === "snoozed" && macro.snoozeDuration
        ? "Status: Snoozed"
        : `Status: ${macro.statusToSet}`;
    badges.push({ key: MacroAutomation.SET_STATUS, label: statusLabel });
  }

  if (macro.tagsToAdd?.length) {
    for (const tag of macro.tagsToAdd) {
      badges.push({ key: `tag-${tag}`, label: `Tag: ${tag}` });
    }
  }

  if (macro.emailSubjectToSet) {
    badges.push({
      key: MacroAutomation.CHANGE_EMAIL_SUBJECT,
      label: `Subject: ${macro.emailSubjectToSet}`,
    });
  }

  if (macro.shouldAddNote) {
    badges.push({
      key: MacroAutomation.ADD_INTERNAL_NOTE,
      label: "Add internal note",
    });
  }

  if (macro.shouldForwardMessage) {
    badges.push({
      key: MacroAutomation.FORWARD_MESSAGE,
      label: `Forward to: ${macro.messageToForwardEmailAddress ?? ""}`,
    });
  }

  return (
    <Flex dir="row" gap="md" wrap="wrap">
      {badges.map((badge) => (
        <RedoBadge key={badge.key} text={badge.label} />
      ))}
    </Flex>
  );
});

const MacroSidebar = memo(function MacroSidebar({
  macros,
  searchValue,
  setSearchValue,
  macroFocused,
  macroSelected,
}: {
  macros: readonly Macro[];
  searchValue: string;
  setSearchValue(value: string): void;
  macroFocused(macro: Macro): void;
  macroSelected(macro: Macro): void;
}) {
  const [focusedMacroIndex, setFocusedMacroIndex] = useState<number | null>(0);
  const [selectedMacroIndex, setSelectedMacroIndex] = useState<number | null>(
    null,
  );
  const [prevSearchValue, setPrevSearchValue] = useState(searchValue);

  const filteredMacros = useSearch(searcher, [...macros], searchValue);

  const searchRef = useRef<HTMLInputElement>(null);

  // Reset focused/selected indexes when search changes (derived state adjustment during render)
  if (searchValue !== prevSearchValue) {
    setPrevSearchValue(searchValue);
    setFocusedMacroIndex(0);
    setSelectedMacroIndex(null);
  }

  function handleMacroSelected({
    item,
    source,
  }: {
    item: RedoListItem<Macro>;
    source: RedoListItemSelectedSource;
  }) {
    const index = filteredMacros.findIndex((m) => m.id === item.value.id);
    setSelectedMacroIndex(index);
    macroFocused(item.value);

    if (source === "keyboard") {
      macroSelected(item.value);
    }
  }

  const selectedItem =
    selectedMacroIndex !== null
      ? filteredMacros[selectedMacroIndex]?.id
      : undefined;

  const templateListItems: RedoListItem<Macro>[] = useMemo(
    () =>
      filteredMacros.map((macro) => ({
        id: macro.id,
        type: "text" as const,
        text: macro.name,
        value: macro,
      })),
    [filteredMacros],
  );

  return (
    <Flex dir="column" flex="1" px="md">
      <Flex align="center" dir="row" justify="space-between">
        <Text fontWeight="semibold" textColor="secondary">
          Templates
        </Text>
      </Flex>
      <Flex pb="md">
        <RedoTextInput
          autoFocus
          fullWidth
          placeholder="Search"
          ref={searchRef}
          setValue={setSearchValue}
          value={searchValue}
        />
      </Flex>
      <Flex overflowY="auto">
        <RedoList
          focusedIndex={focusedMacroIndex ?? undefined}
          items={templateListItems}
          itemSelected={handleMacroSelected}
          refToListenTo={searchRef.current}
          selectedItems={selectedItem}
          setFocusedIndex={(index) => {
            const newValue =
              typeof index === "function"
                ? index(focusedMacroIndex || undefined)
                : index;
            if (newValue === undefined) return;
            setFocusedMacroIndex(newValue);
            const macro = filteredMacros[newValue];
            if (macro) {
              macroFocused(macro);
            }
          }}
          size="md"
        />
      </Flex>
    </Flex>
  );
});

function highlightMacroVariables(html: string): string {
  return html.replace(
    /\{[^}]+\}/g,
    (match) =>
      `<span style="background-color: var(--redo-colors-background-bg-quaternary); color: var(--redo-colors-foreground-fg-secondary); border-radius: var(--redo-corner-xsmall-radius); padding: 2px 6px;">${match}</span>`,
  );
}

function macroToAutomationsData(macro: Macro): MacroAutomationsData {
  return {
    tagsToAdd: macro.tagsToAdd ? [...macro.tagsToAdd] : undefined,
    statusToSet: (macro.statusToSet as MacroStatusToSet) ?? undefined,
    snoozeDuration: (macro.snoozeDuration as SnoozeDuration) ?? undefined,
    emailSubjectToSet: macro.emailSubjectToSet ?? undefined,
    shouldAddNote: macro.shouldAddNote || undefined,
    noteToAddContent: macro.noteToAddContent ?? undefined,
    noteToAddHtmlContent: macro.noteToAddHtmlContent ?? undefined,
    noteToAddUsersMentioned: macro.noteToAddUsersMentioned
      ? [...macro.noteToAddUsersMentioned]
      : undefined,
    shouldForwardMessage: macro.shouldForwardMessage || undefined,
    messageToForwardExtraContent:
      macro.messageToForwardExtraContent ?? undefined,
    messageToForwardEmailAddress:
      macro.messageToForwardEmailAddress ?? undefined,
  };
}
